import React, { useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import PanelManager from "./PanelManager/PanelManager";
import TitleBar from "@common/components/TitleBar/TitleBar";
import LoadingSession from "./LoadingSession/LoadingSession";
import KegCleaning from "./KegCleaning/KegCleaning";
import BrewTimeline from "../../components/BrewTimeline/BrewTimeline";
import ControlSession from "./ControlSession/ControlSession";
import BrewSetupModal from "./ModalManager/Modals/BrewSetupModal";
import * as sessionService from "@brewery/services/sessionService";
import * as mqttActions from "@common/actions/mqttActions";
import mqttService from "../../mqttService";
import mqttUtil from "../../mqttUtil";
import { routes } from "@common/constants/urls";
import {
  ACTIVE_SESSION_STATES,
  INACTIVE_SESSION_STATES,
} from "@common/constants/sessionConstants";
import { isCleaningSession } from "@monitor/util";
import NOTIFICATION_TYPES from "@common/constants/notificationTypes";
import "./monitoring.scss";

/**
 * Manages the fetching of the session and passes the session down
 * to child components
 * @param {Object} props
 */
const Monitor = (props) => {
  const [session, setSession] = useState(null);
  const [fetchingSession, setFetchingSession] = useState(true);
  const [activeProcess, setActiveProcess] = useState(null);
  const [lastLog, setLastLog] = useState(null);
  const [waitingForLog, setWaitingForLog] = useState(true);
  const [showBrewSetup, setShowBrewSetup] = useState(false);
  const currentSessionRef = useRef(null);
  const notificationSubscriptionRef = useRef(null);
  const sessionID = props.match.params.id;

  useEffect(() => {
    fetchSession();
    listenToSessionStateChange();
    return () => {
      if (notificationSubscriptionRef.current) {
        notificationSubscriptionRef.current.unsubscribe();
      }
    };
  }, [sessionID]);

  const fetchSession = async () => {
    props.ignoreNotifications(sessionID);

    try {
      setFetchingSession(true);
      const response = await sessionService.getSessionById(sessionID);
      setFetchingSession(false);

      // Check if the new session state is inactive and the previous session state was active
      // if true, this means that the session got finished/failed
      if (
        currentSessionRef.current !== null &&
        ACTIVE_SESSION_STATES.includes(currentSessionRef.current.status) &&
        INACTIVE_SESSION_STATES.includes(response.status)
      ) {
        props.routeTo(routes.brewery);
      } else {
        setSession(response);
        currentSessionRef.current = response;
      }
    } catch (error) {
      setFetchingSession(false);
      props.routeTo(routes.brewery);
    }
  };

  const listenToSessionStateChange = () => {
    notificationSubscriptionRef.current = mqttService.notification$.subscribe(
      (notification) => {
        if (
          currentSessionRef.current &&
          currentSessionRef.current.id === notification.session
        ) {
          if (
            mqttUtil.sessionStateChanged(notification) ||
            notification.type === NOTIFICATION_TYPES.NOT_PROCESS_STATE_CHANGED
          ) {
            mqttService.unsubscribeAllFrequentLog();
            fetchSession();
          }
        }
      },
    );
  };

  const handleStatusChange = ({ lastLog, activeProcess, waitingForLog }) => {
    setLastLog(lastLog);
    setActiveProcess(activeProcess);
    if (waitingForLog !== undefined) {
      setWaitingForLog(waitingForLog);
    }
  };

  const sessionCleaning = isCleaningSession(session);

  const subtitle = () => {
    if (sessionCleaning) {
      return "";
    } else if (session && session.is_brewpack) {
      return (
        <div className="monitor-subtitle-brewpack">
          <div>
            <img
              className="circular--landscape"
              src={session.beer.image}
              alt="beerimage"
            />
          </div>
          <div className="monitor-subtitle-brewpack__text">
            <div className="monitor-subtitle-brewpack__name">
              {session.beer.name}
            </div>
            <small className="monitor-subtitle-brewpack__style">
              {session.beer.style_name}
            </small>
          </div>
        </div>
      );
    } else if (session) {
      const deviceLabel = session.device.custom_name || session.device.uuid;
      // Custom fermentation sessions carry no recipe/beer (session.beer is null),
      // so fall back to a plain label instead of dereferencing beer.name.
      if (!session.beer) {
        return `Custom fermentation on ${deviceLabel}`;
      }
      return `${session.beer.name} version ${session.beer_recipe_version} on ${deviceLabel}`;
    }
  };

  const kegNeedsCleaning = session ? session.device.user_action === 38 : false;

  const isBrewingSession =
    session &&
    (session.process_type === 1 ||
      (session.device && session.device.device_type === 0));

  const canShowBrewSetup =
    session && !sessionCleaning && isBrewingSession && session.beer_recipe_id;

  const canShowRecipe =
    session && !session.is_brewpack && !sessionCleaning && session.beer;

  const showControls =
    session && activeProcess !== null && activeProcess !== 0 && !waitingForLog;

  // REST API loading happens BEFORE the layout mounts
  if (fetchingSession) {
    return <LoadingSession />;
  }

  return (
    <div className="monitor-page">
      <TitleBar
        title={sessionCleaning ? "CLEANING" : "MONITORING"}
        subtitle={subtitle()}
      >
        {(canShowBrewSetup || canShowRecipe) && (
          <div className="monitor-titlebar-actions">
            {canShowBrewSetup && (
              <Button bsStyle="primary" onClick={() => setShowBrewSetup(true)}>
                BREW SETUP
              </Button>
            )}
            {canShowRecipe && (
              <Button
                bsStyle="warning"
                onClick={() =>
                  window.open(
                    routes.recipeDetailsWithParams(
                      session.beer.id,
                      session.beer_recipe_version,
                    ),
                    "_blank",
                  )
                }
              >
                RECIPE
              </Button>
            )}
          </div>
        )}
      </TitleBar>
      <BrewSetupModal
        show={showBrewSetup}
        handleClose={() => setShowBrewSetup(false)}
        session={session}
      />
      {kegNeedsCleaning ? (
        <KegCleaning session={session} kegNeedsCleaning={kegNeedsCleaning} />
      ) : (
        <div className="monitor-layout-wrapper">
          {showControls && (
            <div className="monitor-controls-row">
              <ControlSession currentLog={lastLog} session={session} />
            </div>
          )}
          <div className="monitor-layout">
            {showControls && !fetchingSession && (
              <BrewTimeline session={session} />
            )}
            <div className="monitor-layout__content">
              <PanelManager
                session={session}
                onStatusChange={handleStatusChange}
                routeTo={props.routeTo}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    ignoreNotifications: (sessionID) =>
      dispatch(mqttActions.ignoreNotifications(sessionID)),
    routeTo: (route) => dispatch(push(route)),
    dispatch: (action) => dispatch(action),
  };
};

export default connect(null, mapDispatchToProps)(Monitor);
